import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, EyeIcon, DownloadIcon, FilterIcon, CreditCardIcon, QrCodeIcon, SmartphoneIcon, FileTextIcon, TerminalIcon, RefreshCwIcon, MonitorIcon, XIcon } from 'lucide-react';
import { Sale, TransactionSource, TransactionStatus } from '../types/Transaction';
export function Sales() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  // Mock data - in a real app, this would come from an API
  const sales: Sale[] = [{
    id: '1',
    transactionId: 'TXN-2024-001234',
    amount: 15000,
    currency: 'LKR',
    status: 'completed',
    source: 'payment_link',
    date: '2024-01-15T10:30:00',
    customerName: 'Nimal Perera',
    customerEmail: 'nimal@example.com',
    customerPhone: '+94 77 123 4567',
    paymentMethod: {
      type: 'card',
      last4: '4242',
      brand: 'Visa'
    },
    merchantName: 'ABC Store',
    merchantId: 'MERCH-001',
    description: 'Online purchase',
    fee: 450,
    netAmount: 14550,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T10:30:00',
      status: 'initiated',
      description: 'Transaction initiated'
    }, {
      id: '2',
      timestamp: '2024-01-15T10:30:15',
      status: 'processing',
      description: 'Payment processing'
    }, {
      id: '3',
      timestamp: '2024-01-15T10:30:45',
      status: 'completed',
      description: 'Payment successful'
    }]
  }, {
    id: '2',
    transactionId: 'TXN-2024-001235',
    amount: 8500,
    currency: 'LKR',
    status: 'completed',
    source: 'qr_code',
    date: '2024-01-15T11:45:00',
    customerName: 'Kamala Silva',
    customerEmail: 'kamala@example.com',
    paymentMethod: {
      type: 'wallet',
      details: 'eZ Cash'
    },
    merchantName: 'XYZ Restaurant',
    merchantId: 'MERCH-002',
    description: 'Dine-in payment',
    fee: 255,
    netAmount: 8245,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T11:45:00',
      status: 'initiated',
      description: 'QR code scanned'
    }, {
      id: '2',
      timestamp: '2024-01-15T11:45:10',
      status: 'completed',
      description: 'Payment successful'
    }]
  }, {
    id: '3',
    transactionId: 'TXN-2024-001236',
    amount: 25000,
    currency: 'LKR',
    status: 'refunded',
    source: 'invoice',
    date: '2024-01-14T14:20:00',
    customerName: 'Saman Fernando',
    customerEmail: 'saman@example.com',
    customerPhone: '+94 71 987 6543',
    paymentMethod: {
      type: 'card',
      last4: '5555',
      brand: 'Mastercard'
    },
    merchantName: 'Tech Solutions Ltd',
    merchantId: 'MERCH-003',
    description: 'Software subscription',
    invoiceId: 'INV-2024-001',
    refundAmount: 25000,
    fee: 750,
    netAmount: 0,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-14T14:20:00',
      status: 'initiated',
      description: 'Invoice payment initiated'
    }, {
      id: '2',
      timestamp: '2024-01-14T14:20:30',
      status: 'completed',
      description: 'Payment successful'
    }, {
      id: '3',
      timestamp: '2024-01-15T09:15:00',
      status: 'refund_initiated',
      description: 'Refund requested'
    }, {
      id: '4',
      timestamp: '2024-01-15T09:20:00',
      status: 'refunded',
      description: 'Refund completed'
    }]
  }, {
    id: '4',
    transactionId: 'TXN-2024-001237',
    amount: 3500,
    currency: 'LKR',
    status: 'completed',
    source: 'tap_to_phone',
    date: '2024-01-15T16:30:00',
    customerName: 'Priya Jayawardena',
    customerEmail: 'priya@example.com',
    paymentMethod: {
      type: 'card',
      last4: '8888',
      brand: 'Visa'
    },
    merchantName: 'Coffee Shop',
    merchantId: 'MERCH-004',
    description: 'Contactless payment',
    fee: 105,
    netAmount: 3395,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T16:30:00',
      status: 'initiated',
      description: 'Card tapped'
    }, {
      id: '2',
      timestamp: '2024-01-15T16:30:05',
      status: 'completed',
      description: 'Payment successful'
    }]
  }, {
    id: '5',
    transactionId: 'TXN-2024-001238',
    amount: 45000,
    currency: 'LKR',
    status: 'failed',
    source: 'pos',
    date: '2024-01-15T17:00:00',
    customerName: 'Ranil Wickramasinghe',
    customerEmail: 'ranil@example.com',
    paymentMethod: {
      type: 'card',
      last4: '3333',
      brand: 'Mastercard'
    },
    merchantName: 'Electronics Store',
    merchantId: 'MERCH-005',
    description: 'POS terminal payment',
    fee: 0,
    netAmount: 0,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T17:00:00',
      status: 'initiated',
      description: 'POS transaction initiated'
    }, {
      id: '2',
      timestamp: '2024-01-15T17:00:10',
      status: 'processing',
      description: 'Contacting bank'
    }, {
      id: '3',
      timestamp: '2024-01-15T17:00:20',
      status: 'failed',
      description: 'Insufficient funds'
    }]
  }];
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSourceFilter('all');
    setPaymentMethodFilter('all');
    setDateFrom('');
    setDateTo('');
    setAmountMin('');
    setAmountMax('');
  };
  const hasActiveFilters = searchTerm !== '' || statusFilter !== 'all' || sourceFilter !== 'all' || paymentMethodFilter !== 'all' || dateFrom !== '' || dateTo !== '' || amountMin !== '' || amountMax !== '';
  const getSourceIcon = (source: TransactionSource) => {
    const icons = {
      payment_link: <CreditCardIcon size={16} data-id="element-5621" />,
      qr_code: <QrCodeIcon size={16} data-id="element-5622" />,
      tap_to_phone: <SmartphoneIcon size={16} data-id="element-5623" />,
      invoice: <FileTextIcon size={16} data-id="element-5624" />,
      pos: <TerminalIcon size={16} data-id="element-5625" />,
      recurring: <RefreshCwIcon size={16} data-id="element-5626" />,
      virtual_terminal: <MonitorIcon size={16} data-id="element-5627" />
    };
    return icons[source];
  };
  const getSourceLabel = (source: TransactionSource) => {
    const labels = {
      payment_link: 'Payment Link',
      qr_code: 'QR Code',
      tap_to_phone: 'Tap to Phone',
      invoice: 'Invoice',
      pos: 'POS Terminal',
      recurring: 'Recurring',
      virtual_terminal: 'Virtual Terminal'
    };
    return labels[source];
  };
  const getStatusBadge = (status: TransactionStatus) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800',
      partially_refunded: 'bg-orange-100 text-orange-800',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-5628">
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>;
  };
  const filteredSales = sales.filter(sale => {
    const matchesSearch = sale.transactionId.toLowerCase().includes(searchTerm.toLowerCase()) || sale.customerName.toLowerCase().includes(searchTerm.toLowerCase()) || sale.customerEmail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || sale.status === statusFilter;
    const matchesSource = sourceFilter === 'all' || sale.source === sourceFilter;
    const matchesPaymentMethod = paymentMethodFilter === 'all' || sale.paymentMethod.type === paymentMethodFilter;
    const saleDate = new Date(sale.date);
    const matchesDateFrom = !dateFrom || saleDate >= new Date(dateFrom);
    const matchesDateTo = !dateTo || saleDate <= new Date(dateTo + 'T23:59:59');
    const matchesAmountMin = !amountMin || sale.amount >= parseFloat(amountMin);
    const matchesAmountMax = !amountMax || sale.amount <= parseFloat(amountMax);
    return matchesSearch && matchesStatus && matchesSource && matchesPaymentMethod && matchesDateFrom && matchesDateTo && matchesAmountMin && matchesAmountMax;
  });
  const totalVolume = filteredSales.reduce((sum, sale) => sum + sale.amount, 0);
  const totalTransactions = filteredSales.length;
  const successfulTransactions = filteredSales.filter(s => s.status === 'completed').length;
  const successRate = totalTransactions > 0 ? Math.round(successfulTransactions / totalTransactions * 100) : 0;
  return <div className="space-y-6" data-id="element-5629">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-5630">
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-5631">
          <div className="text-sm font-medium text-gray-500" data-id="element-5632">Total Volume</div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-5633">
            Rs. {totalVolume.toLocaleString('en-LK')}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-5634">
          <div className="text-sm font-medium text-gray-500" data-id="element-5635">
            Total Transactions
          </div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-5636">
            {totalTransactions}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-5637">
          <div className="text-sm font-medium text-gray-500" data-id="element-5638">Successful</div>
          <div className="mt-2 text-3xl font-semibold text-green-600" data-id="element-5639">
            {successfulTransactions}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-5640">
          <div className="text-sm font-medium text-gray-500" data-id="element-5641">Success Rate</div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-5642">
            {successRate}%
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-5643">
        {/* Header */}
        <div className="p-6 border-b border-gray-200" data-id="element-5644">
          <div className="space-y-4" data-id="element-5645">
            {/* Primary Filters Row */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-5646">
              <div className="flex-1 max-w-lg" data-id="element-5647">
                <div className="relative" data-id="element-5648">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} data-id="element-5649" />
                  <input type="text" placeholder="Search by transaction ID, customer name, or email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-5650" />
                </div>
              </div>
              <div className="flex items-center gap-3" data-id="element-5651">
                <button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)} className={`flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 ${showAdvancedFilters ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`} data-id="element-5652">
                  <FilterIcon size={18} data-id="element-5653" />
                  Filters
                  {hasActiveFilters && <span className="ml-1 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full" data-id="element-5654">
                      {[searchTerm, statusFilter !== 'all', sourceFilter !== 'all', paymentMethodFilter !== 'all', dateFrom, dateTo, amountMin, amountMax].filter(Boolean).length}
                    </span>}
                </button>
                <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-5655">
                  <DownloadIcon size={18} data-id="element-5656" />
                  Export
                </button>
              </div>
            </div>
            {/* Advanced Filters Panel */}
            {showAdvancedFilters && <div className="p-4 bg-gray-50 rounded-lg space-y-4" data-id="element-5657">
                <div className="flex items-center justify-between" data-id="element-5658">
                  <h3 className="text-sm font-medium text-gray-700" data-id="element-5659">
                    Advanced Filters
                  </h3>
                  {hasActiveFilters && <button onClick={clearFilters} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700" data-id="element-5660">
                      <XIcon size={16} data-id="element-5661" />
                      Clear all
                    </button>}
                </div>
                {/* Filter Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-5662">
                  {/* Status Filter */}
                  <div data-id="element-5663">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5664">
                      Status
                    </label>
                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-5665">
                      <option value="all" data-id="element-5666">All Status</option>
                      <option value="completed" data-id="element-5667">Completed</option>
                      <option value="pending" data-id="element-5668">Pending</option>
                      <option value="processing" data-id="element-5669">Processing</option>
                      <option value="failed" data-id="element-5670">Failed</option>
                      <option value="refunded" data-id="element-5671">Refunded</option>
                    </select>
                  </div>
                  {/* Source Filter */}
                  <div data-id="element-5672">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5673">
                      Source
                    </label>
                    <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-5674">
                      <option value="all" data-id="element-5675">All Sources</option>
                      <option value="payment_link" data-id="element-5676">Payment Link</option>
                      <option value="qr_code" data-id="element-5677">QR Code</option>
                      <option value="tap_to_phone" data-id="element-5678">Tap to Phone</option>
                      <option value="invoice" data-id="element-5679">Invoice</option>
                      <option value="pos" data-id="element-5680">POS Terminal</option>
                      <option value="recurring" data-id="element-5681">Recurring</option>
                      <option value="virtual_terminal" data-id="element-5682">Virtual Terminal</option>
                    </select>
                  </div>
                  {/* Payment Method Filter */}
                  <div data-id="element-5683">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5684">
                      Payment Method
                    </label>
                    <select value={paymentMethodFilter} onChange={e => setPaymentMethodFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-5685">
                      <option value="all" data-id="element-5686">All Methods</option>
                      <option value="card" data-id="element-5687">Card</option>
                      <option value="wallet" data-id="element-5688">Wallet</option>
                      <option value="bank_transfer" data-id="element-5689">Bank Transfer</option>
                      <option value="cash" data-id="element-5690">Cash</option>
                    </select>
                  </div>
                  {/* Date From */}
                  <div data-id="element-5691">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5692">
                      Date From
                    </label>
                    <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-5693" />
                  </div>
                  {/* Date To */}
                  <div data-id="element-5694">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5695">
                      Date To
                    </label>
                    <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-5696" />
                  </div>
                  {/* Amount Min */}
                  <div data-id="element-5697">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5698">
                      Min Amount (Rs.)
                    </label>
                    <input type="number" placeholder="0" value={amountMin} onChange={e => setAmountMin(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-5699" />
                  </div>
                  {/* Amount Max */}
                  <div data-id="element-5700">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-5701">
                      Max Amount (Rs.)
                    </label>
                    <input type="number" placeholder="No limit" value={amountMax} onChange={e => setAmountMax(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-5702" />
                  </div>
                </div>
              </div>}
          </div>
        </div>
        {/* Table */}
        <div className="overflow-x-auto" data-id="element-5703">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-5704">
            <thead className="bg-gray-50" data-id="element-5705">
              <tr data-id="element-5706">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5707">
                  Transaction ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5708">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5709">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5710">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5711">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5712">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5713">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-5714">
              {filteredSales.map(sale => <tr key={sale.id} className="hover:bg-gray-50" data-id="element-5715">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5716">
                    <div className="text-sm font-medium text-gray-900" data-id="element-5717">
                      {sale.transactionId}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5718">
                    <div className="text-sm text-gray-900" data-id="element-5719">
                      {new Date(sale.date).toLocaleDateString('en-GB')}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-5720">
                      {new Date(sale.date).toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5721">
                    <div className="text-sm text-gray-900" data-id="element-5722">
                      {sale.customerName}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-5723">
                      {sale.customerEmail}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5724">
                    <div className="flex items-center gap-2 text-sm text-gray-900" data-id="element-5725">
                      {getSourceIcon(sale.source)}
                      {getSourceLabel(sale.source)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5726">
                    <div className="text-sm font-medium text-gray-900" data-id="element-5727">
                      Rs. {sale.amount.toLocaleString('en-LK')}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-5728">
                      Net: Rs. {sale.netAmount.toLocaleString('en-LK')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5729">
                    {getStatusBadge(sale.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-5730">
                    <button onClick={() => navigate(`/sale-detail/${sale.id}`)} className="text-blue-600 hover:text-blue-900" title="View Details" data-id="element-5731">
                      <EyeIcon size={18} data-id="element-5732" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {/* Empty State */}
        {filteredSales.length === 0 && <div className="text-center py-12" data-id="element-5733">
            <p className="text-gray-500" data-id="element-5734">No transactions found</p>
          </div>}
      </div>
    </div>;
}